/**
 * Ajax Search Block - Interactivity API View Script
 *
 * Live type-ahead search with debounced REST API fetching.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

// Keep in sync with constants.js.
const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

/**
 * Escape HTML entities to prevent XSS in rendered results.
 *
 * @param {string} str - Raw string.
 * @return {string} Escaped string.
 */
const escapeHtml = ( str ) => {
	const div = document.createElement( 'div' );
	div.appendChild( document.createTextNode( str ) );
	return div.innerHTML;
};

/**
 * Build HTML for a single result item.
 *
 * @param {Object}  item       - Result object from REST API.
 * @param {boolean} showImages - Whether to show thumbnails.
 * @param {boolean} showPrice  - Whether to show price.
 * @return {string} HTML string.
 */
const buildResultItemHtml = ( item, showImages, showPrice ) => {
	const imageHtml =
		showImages && item.image
			? `<img class="brandy-ajax-search__result-image" src="${ escapeHtml(
					item.image
			  ) }" alt="${ escapeHtml( item.title ) }" loading="lazy" />`
			: showImages
			? `<div class="brandy-ajax-search__result-image brandy-ajax-search__result-image--placeholder"></div>`
			: '';

	const priceHtml =
		showPrice && item.price
			? `<span class="brandy-ajax-search__result-price">${ item.price }</span>`
			: '';

	return `<a class="brandy-ajax-search__result-item" href="${ escapeHtml(
		item.url
	) }">
		${ imageHtml }
		<div class="brandy-ajax-search__result-content">
			<span class="brandy-ajax-search__result-title">${ escapeHtml(
				item.title
			) }</span>
			<span class="brandy-ajax-search__result-excerpt">${ escapeHtml(
				item.excerpt
			) }</span>
			${ priceHtml }
		</div>
	</a>`;
};
/**
 * Perform search request to REST API.
 *
 * @param {Object} ctx - Interactivity context.
 */
async function performSearch( ctx ) {
	const term = ctx._searchTerm;

	// Abort any in-flight request.
	if ( ctx._abortController ) {
		ctx._abortController.abort();
	}

	ctx._abortController = new AbortController();
	ctx._isLoading = true;
	ctx._isOpen = true;
	ctx._searchDone = false;

	try {
		const url = new URL( ctx.restUrl );
		url.searchParams.set( 's', term );
		url.searchParams.set( 'post_types', ctx.postTypes );
		url.searchParams.set( 'limit', String( ctx.limit ) );

		const response = await fetch( url.toString(), {
			signal: ctx._abortController.signal,
			headers: {
				'Content-Type': 'application/json',
			},
		} );

		if ( ! response.ok ) {
			throw new Error( `HTTP ${ response.status }` );
		}

		const data = await response.json();
		
		// Only update if the term hasn't changed during fetch.
		if ( ctx._searchTerm === term ) {
			ctx._results = data.results || [];
			ctx._hasMore = data.hasMore || false;
			ctx._searchDone = true;
			ctx._isLoading = false;
		}
	} catch ( error ) {
		// Ignore abort errors (user typed again).
		if ( error.name !== 'AbortError' ) {
			ctx._isLoading = false;
			ctx._searchDone = true;
			ctx._results = [];
		}
	}
}

/**
 * Search block store.
 */
store( 'brandy/ajax-search', {
	/**
	 * Reactive state getters.
	 */
	state: {
		get isOpen() {
			const ctx = getContext();
			return ctx._isOpen || false;
		},

		get isLoading() {
			const ctx = getContext();
			return ctx._isLoading || false;
		},

		get searchTerm() {
			const ctx = getContext();
			return ctx._searchTerm || '';
		},

		get hasMore() {
			const ctx = getContext();
			return ctx._hasMore || false;
		},

		get viewAllUrl() {
			const ctx = getContext();
			const term = encodeURIComponent( ctx._searchTerm || '' );
			return `${ ctx.searchUrl }?s=${ term }`;
		},

		get showNoResults() {
			const ctx = getContext();
			return (
				ctx._searchDone === true &&
				( ctx._results || [] ).length === 0 &&
				! ctx._isLoading
			);
		},
	},

	/**
	 * Actions for user interactions.
	 */
	actions: {
		/**
		 * Handle input changes with debounce.
		 */
		handleInput() {
			const ctx = getContext();
			const { ref } = getElement();
			const value = ref.value;

			ctx._searchTerm = value;

			// Clear existing debounce timer.
			if ( ctx._debounceTimer ) {
				clearTimeout( ctx._debounceTimer );
			}

			// Close and reset if input too short.
			if ( value.length < MIN_SEARCH_LENGTH ) {
				ctx._isOpen = false;
				ctx._results = [];
				ctx._hasMore = false;
				ctx._searchDone = false;
				return;
			}

			// Debounce the search.
			ctx._debounceTimer = setTimeout( () => {
				performSearch( ctx );
			}, DEBOUNCE_MS );
		},

		/**
		 * Handle input focus - reopen results if available.
		 */
		handleFocus() {
			const ctx = getContext();
			if (
				( ctx._results || [] ).length > 0 &&
				( ctx._searchTerm || '' ).length >= MIN_SEARCH_LENGTH
			) {
				ctx._isOpen = true;
			}
		},

		/**
		 * Handle form submit - navigate to full search results page.
		 */
		handleSubmit( event ) {
			event.preventDefault();
			const ctx = getContext();
			const term = ctx._searchTerm || '';

			if ( term.length > 0 ) {
				window.location.href = `${ ctx.searchUrl }?s=${ encodeURIComponent(
					term
				) }`;
			}
		},

		/**
		 * Handle suggestion keyword chip click.
		 */
		handleSuggestionClick() {
			const ctx = getContext();
			const { ref } = getElement();
			const keyword = ref.getAttribute( 'data-keyword' );

			if ( ! keyword ) {
				return;
			}

			ctx._searchTerm = keyword;
			ctx._isOpen = true;

			// Update the input element value.
			const wrapper = ref.closest( '.brandy-ajax-search-wrapper' );
			const input = wrapper?.querySelector( '.brandy-ajax-search__input' );
			if ( input ) {
				input.value = keyword;
				input.focus();
			}

			// Search immediately (no debounce for suggestion clicks).
			performSearch( ctx );
		},

		/**
		 * Close dropdown on click outside.
		 */
		handleClickOutside( event ) {
			const ctx = getContext();
			const { ref } = getElement();

			if ( ! ref.contains( event.target ) ) {
				ctx._isOpen = false;
			}
		},

		/**
		 * Handle global keydown - Escape to close.
		 */
		handleGlobalKeydown( event ) {
			if ( event.key === 'Escape' ) {
				const ctx = getContext();
				ctx._isOpen = false;
			}
		},
	},

	/**
	 * Lifecycle callbacks.
	 */
	callbacks: {
		/**
		 * Initialize block instance state.
		 */
		init() {
			const ctx = getContext();
			ctx._searchTerm = '';
			ctx._results = [];
			ctx._isOpen = false;
			ctx._isLoading = false;
			ctx._hasMore = false;
			ctx._searchDone = false;
			ctx._debounceTimer = null;
			ctx._abortController = null;
		},

		/**
		 * Watch results and render HTML into the results list.
		 * Uses data-wp-watch instead of data-wp-html for broader WP compatibility.
		 */
		renderResults() {
			const ctx = getContext();
			const { ref } = getElement();
			const results = ctx._results || [];

			if ( results.length === 0 ) {
				ref.innerHTML = '';
				return;
			}

			ref.innerHTML = results
				.map( ( item ) =>
					buildResultItemHtml(
						item,
						ctx.showImages,
						ctx.showPrice
					)
				)
				.join( '' );
		},
	},
} );
